import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../auth/AuthContext";
import { Seo } from "../components/Seo";
import { Logo } from "../components/landing/Logo";

export const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== true) navigate("/admin", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const err = params.get("error");
    if (err) toast.error(err);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors duration-300 focus:border-foreground";

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-6">
      <Seo
        title="Team Login | Interlocked Solutions Leads Dashboard"
        description="Secure sign-in for authorised Interlocked Solutions staff to access the FireFlink leads dashboard and manage demo requests."
        path="/login"
        noindex
      />
      <div className="absolute inset-0 grid-lines opacity-40" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 lg:p-10"
      >
        <a href="/" className="inline-flex" data-testid="login-logo-home">
          <Logo className="h-12" />
        </a>

        <div className="mt-8 mb-8">
          <span className="font-mono-ui text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
            Team Access
          </span>
          <h1 className="mt-3 font-heading text-3xl font-medium tracking-tight">
            Leads Dashboard
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Welcome to the Interlocked Solutions team portal. Sign in with your work email and
            password to review incoming FireFlink demo requests, track which enquiries are new or
            already handled, and follow up with prospects. This area is restricted to authorised
            Interlocked Solutions staff only.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            If you have forgotten your password or need an account created, please contact your
            administrator at{" "}
            <a
              href="mailto:info@interlockedsolutions.co.uk"
              className="text-primary underline underline-offset-2"
            >
              info@interlockedsolutions.co.uk
            </a>
            . Looking for FireFlink automation testing instead?{" "}
            <a href="/" className="text-primary underline underline-offset-2">
              Return to our homepage
            </a>{" "}
            to book a demo.
          </p>
        </div>

        <form onSubmit={submit} data-testid="login-form" className="space-y-6">
          <input
            data-testid="login-email"
            type="email"
            className={inputCls}
            placeholder="Work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            data-testid="login-password"
            type="password"
            className={inputCls}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </button>
        </form>

        <p className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
          <Lock size={12} strokeWidth={1.5} /> Restricted to authorised Interlocked Solutions staff.
        </p>
      </motion.div>
    </div>
  );
};
